import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikErrors, FormikTouched } from 'formik';
import classnames from 'classnames';

import { classifiedSort } from '@/utils/name';
import { Label } from '@/components/shared/Label';
import { SharedButton } from '@/components/shared/Button';
import * as styles from './index.module.scss';

export type Values = {
  firstName: string;
  lastName: string;
  kanaFirstName: string;
  kanaLastName: string;
};

type Props = {
  errors: FormikErrors<Values>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  dirty: boolean;
  isSubmitting: boolean;
  touched: FormikTouched<Values>;
  values: Values;
};

export const FormContents: React.FC<Props> = ({
  errors,
  handleSubmit,
  handleChange,
  isLoading,
  dirty,
  isSubmitting,
  touched,
  values,
}) => {
  const { t } = useTranslation();
  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <label className={styles.label}>
        {t('models.contact.personInCharge')}
      </label>
      <div className={styles.formGroup}>
        {classifiedSort([
          <div key="first-name" className={styles.formField}>
            <input
              data-test="first-name"
              className={classnames(styles.input, 'field', {
                [styles.error]: errors.firstName,
              })}
              placeholder={t('ui.expression.firstName')}
              name="firstName"
              onChange={handleChange}
              value={values.firstName}
            />
            {touched.firstName && errors.firstName && (
              <Label classNames={'parallel-elem'}>{errors.firstName}</Label>
            )}
          </div>,
          <div key="last-name" className={styles.formField}>
            <input
              data-test="last-name"
              className={classnames(styles.input, 'field', {
                [styles.error]: errors.lastName,
              })}
              placeholder={t('ui.expression.lastName')}
              name="lastName"
              onChange={handleChange}
              value={values.lastName}
            />
            {touched.lastName && errors.lastName && (
              <Label classNames={'parallel-elem'}>{errors.lastName}</Label>
            )}
          </div>,
        ])}
      </div>

      <label className={styles.label}>
        {t('models.contact.personInCharge')}（{t('ui.expression.phonetic')}）
      </label>
      <div className={styles.formGroup}>
        {classifiedSort([
          <div key="kana-first-name" className={styles.formField}>
            <input
              data-test="kana-first-name"
              className={classnames(styles.input, 'field', {
                [styles.error]: errors.kanaFirstName,
              })}
              placeholder={t('ui.expression.firstNamePhonetic')}
              name="kanaFirstName"
              onChange={handleChange}
              value={values.kanaFirstName}
            />
            {touched.kanaFirstName && errors.kanaFirstName && (
              <Label classNames={'parallel-elem'}>{errors.kanaFirstName}</Label>
            )}
          </div>,
          <div key="kana-last-name" className={styles.formField}>
            <input
              data-test="kana-last-name"
              className={classnames(styles.input, 'field', {
                [styles.error]: errors.kanaLastName,
              })}
              placeholder={t('ui.expression.lastNamePhonetic')}
              name="kanaLastName"
              onChange={handleChange}
              value={values.kanaLastName}
            />
            {touched.kanaLastName && errors.kanaLastName && (
              <Label classNames={'parallel-elem'}>{errors.kanaLastName}</Label>
            )}
          </div>,
        ])}
      </div>

      <div className={styles.submitButton}>
        <SharedButton
          dataTest="update-password"
          disabled={!dirty || isSubmitting || isLoading}
          type="submit"
        >
          {t('ui.action.changeNow')}
        </SharedButton>
      </div>
    </form>
  );
};
